import React, { useMemo } from 'react';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { createActionsColumnDefinition } from './columns/actions';
import { createHeightColumnDefinition } from './columns/height';
import { createLengthColumnDefinition } from './columns/length';
import { createMaxWeightColumnDefinition } from './columns/max-weight';
import { createNameColumnDefinition } from './columns/name';
import { createTareWeightColumnDefinition } from './columns/tare-weight';
import { createWidthColumnDefinition } from './columns/width';
import { EmptyCustomPackagesPlaceholder } from './image/empty-custom-packages-placeholder';
import style from './style.module.css';
import { useEnableCustomPackageWeights } from '../../../../../feature-flags/hooks/use-enable-custom-package-weights';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { PackingRecommendations } from '../../../../../constants/features';

import type { CustomPackagesTableProps } from './custom-packages-table.d';
import type { TableOptions } from 'react-table';

type CustomPackagesTableOptions = TableOptions<Record<string, unknown>> & {
  sellerDefaultDimensionsUnit: CustomPackagesTableProps['sellerDefaultDimensionsUnit'];
  sellerDefaultWeightUnit: CustomPackagesTableProps['sellerDefaultWeightUnit'];
  onEditPackage: CustomPackagesTableProps['onEditPackage'];
  onDeletePackage: CustomPackagesTableProps['onDeletePackage'];
};

export const CustomPackagesTable = ({
  data,
  sellerDefaultDimensionsUnit,
  sellerDefaultWeightUnit,
  onEditPackage,
  onDeletePackage,
}: CustomPackagesTableProps) => {
  const isWeightsEnabled = useEnableCustomPackageWeights();
  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);

  const columns = useMemo(() => {
    const baseColumns = [
      createNameColumnDefinition(),
      createLengthColumnDefinition(),
      createWidthColumnDefinition(),
      createHeightColumnDefinition(),
    ];

    if (isWeightsEnabled) {
      baseColumns.push(createTareWeightColumnDefinition(), createMaxWeightColumnDefinition());
    } else if (isPackingRecommendationsEnabled) {
      baseColumns.push(createMaxWeightColumnDefinition());
    }

    baseColumns.push(createActionsColumnDefinition());
    return baseColumns;
  }, [isWeightsEnabled, isPackingRecommendationsEnabled]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data,
      sellerDefaultDimensionsUnit,
      sellerDefaultWeightUnit,
      onEditPackage,
      onDeletePackage,
    } as CustomPackagesTableOptions,
    useFlexLayout,
  );

  return (
    <ReactTableWrapper
      className={style['custom-packages-table']}
      emptyRow={<EmptyCustomPackagesPlaceholder />}
      getTableBodyProps={getTableBodyProps}
      getTableProps={getTableProps}
      headerGroups={headerGroups}
      headerRowClassName={style['header-row']}
      prepareRow={prepareRow}
      rows={rows}
    />
  );
};
